import * as types from '../mutation-types';
import { Module } from 'vuex';
import moment from 'moment';
import { get, set, pick } from 'lodash';

import apiStudio from '../../api/modules/studios';
import { generateAllCalendarEvents } from '@/core/calendar';
import templates from '@/web/utils/studio-templates';
// import { prefetchImages } from '@/mobile/components/utils/image-cache-it';

interface State {
  recentStudios: StudioPreviewResponse[] | null;
  currentStudio: StudioResponse | null;
  currentLesson: LessonResponse | null;
  studioEvents: EventResponse[] | null;
  upcomingStudioEvents: EventResponse[] | null;
  productMediaFavourites: FavouritesResponse[] | null;
  eventStartingSoon: EventResponse | null;
  translations: TranslationsResponse | null;
  currentTranslationsEditLang: string;
  marvelousBio: BioResponse | null;
}

const initialState: State = {
  recentStudios: null,
  currentStudio: null,
  currentLesson: null,
  studioEvents: null,
  upcomingStudioEvents: null,
  productMediaFavourites: null,
  eventStartingSoon: null,
  translations: null,
  currentTranslationsEditLang: 'en',
  marvelousBio: null,
};

const studioModule: Module<State, any> = {
  namespaced: true,
  state: initialState,
  getters: {
    recentStudios: state => state.recentStudios,
    purchasedProducts: state =>
      state.currentStudio
        ? state.currentStudio.products.filter(p => p.purchased)
        : null,
    currentStudio: state => state.currentStudio,
    selectedTemplate(state, getters, rootState, rootGetters) {
      const previewTemplate = rootGetters['globals/previewTemplate'];
      if (previewTemplate) {
        return templates.find(template => template.id === previewTemplate);
      }
      return templates.find(
        template => template.id === state.currentStudio?.selected_template
      );
    },
    alternativePlaybackEnabled: state =>
      state.currentStudio?.alternative_playback_enabled,
    isOwner: state => state.currentStudio?.is_owner,
    currentStudioId: state => state.currentStudio?.id,
    currentStudioWaiver: state => state.currentStudio?.waiver,
    isTwilioConnected: state => state.currentStudio?.is_twilio_connected,
    currentStudioUrl: state =>
      state.currentStudio ? state.currentStudio.studio_url : '',
    currency: state => state.currentStudio?.currency,
    showSignupLinkForStudio: state => state.currentStudio?.allow_signup,
    todaysEvents: state => {
      if (state.studioEvents) {
        return generateAllCalendarEvents(state.studioEvents).filter(te =>
          moment(te.date_start).isSame(moment(), 'date')
        );
      }
      return null;
    },
    nextEvent: state => {
      if (state.studioEvents) {
        return generateAllCalendarEvents(state.studioEvents).find(te =>
          moment(te.date_end).isSameOrAfter(moment())
        );
      }
      return null;
    },
    currentLesson: state => state.currentLesson,
    previousLessonMediaId: state => currentLessonMediaId => {
      if (!currentLessonMediaId || !state.currentLesson?.content) return;
      const indexOfCurrentLessonMedia = state.currentLesson.content.findIndex(
        media => media.id == currentLessonMediaId
      );
      const previousLessonMediaIndex =
        (indexOfCurrentLessonMedia + state.currentLesson.content.length - 1) %
        state.currentLesson.content.length;
      return state.currentLesson.content[previousLessonMediaIndex]?.id;
    },
    nextLessonMediaId: state => currentLessonMediaId => {
      if (!currentLessonMediaId || !state.currentLesson?.content) return;
      const indexOfCurrentLessonMedia = state.currentLesson.content.findIndex(
        media => media.id == currentLessonMediaId
      );
      const nextLessonMediaIndex =
        (indexOfCurrentLessonMedia + 1) % state.currentLesson.content.length;
      return state.currentLesson.content[nextLessonMediaIndex]?.id;
    },
    studioEvents: state => state.studioEvents,
    upcomingStudioEvents: state => state.upcomingStudioEvents,
    favourites: state => state.productMediaFavourites,
    eventStartingSoon: state => state.eventStartingSoon,
    translations: state => state.translations?.translations,
    mediaIconsEnabled: state => state.currentStudio?.enable_media_icons,
    communityProfileId: state => state.currentStudio?.community_profile_id,
    marvelousBio: state => state.marvelousBio,
    hasStripe: state => state.currentStudio?.payment_gateway === 'stripe',
    hasPayPal: state => state.currentStudio?.payment_gateway === 'paypal',
    googleAnalyticsCode: state => state.currentStudio?.google_analytics_code,
    rewardfulApiKey: state => state.currentStudio?.rewardful_api_key,
    isExpired: state => state.currentStudio?.is_expired,
    hasCustomNavLinks: state => {
      if (state.currentStudio) {
        return !!(
          state.currentStudio.custom_nav_link_1 ||
          state.currentStudio.custom_nav_link_2 ||
          state.currentStudio.custom_nav_link_3
        );
      }
      return false;
    },
  },
  actions: {
    async getRecentStudios({ commit }) {
      const recentStudios = await apiStudio.getRecentStudios();

      commit(types.SET_RECENT_STUDIOS, recentStudios);
    },
    async getStudio({ commit, dispatch, state }, studioURL: string) {
      if (!!studioURL && state.currentStudio?.studio_url != studioURL) {
        // reset the flag when visiting other studios
        dispatch('community/setInteractAsStudio', false, { root: true });
      }

      const studio = await apiStudio.getStudioDetail(studioURL);
      commit(types.SET_CURRENT_STUDIO, studio);
    },
    async getStudioLesson({ commit }, { studioURL, productId, lessonId }) {
      const result = await apiStudio.getLesson(studioURL, productId, lessonId);
      commit(types.SET_LESSON_MEDIA, result);
    },
    async getStudioEvents({ commit }, studioURL: string) {
      let events = await apiStudio.getStudioEvents(studioURL);
      // Remove events without product
      if (events.results) {
        events = events.results;
      }
      commit(types.SET_STUDIO_EVENTS, events);
    },
    async getUpcomingStudioEvents(
      { commit },
      { studioURL, fromDate, isRegistered }
    ) {
      let events = await apiStudio.getUpcomingStudioEvents(
        studioURL,
        moment(fromDate),
        isRegistered
      );
      // Remove events without product
      if (events.results) {
        events = events.results;
      }
      commit(types.SET_UPCOMING_STUDIO_EVENTS, events);
    },
    async getStudioEventsWithFilter(
      { commit },
      { studioURL, fromDate, toDate, isRegistered }
    ) {
      const events = await apiStudio.getStudioEventsWithFilter(
        studioURL,
        fromDate,
        toDate,
        isRegistered
      );
      commit(types.SET_STUDIO_EVENTS, events);
    },
    resetState({ commit }) {
      commit(types.RESET_STUDIO_STATE);
    },
    async sendInvitationEmails(_, payload) {
      await apiStudio.sendInvitationEmails(payload);
    },
    async trackProductMediaView(
      _,
      { studioURL, productId, mediaId, relativeUrl }
    ) {
      await apiStudio.trackProductMediaView(
        studioURL,
        productId,
        mediaId,
        relativeUrl
      );
    },
    async getStudentFavourites({ commit }, studioURL) {
      const favourites = await apiStudio.getStudentFavourites(studioURL);
      commit(types.SET_FAVOURITES, favourites);
    },
    async getInstagramPhotos(_, studioId) {
      return await apiStudio.getInstagramPhotos(studioId);
    },
    clearEventStartingSoon({ commit }) {
      commit(types.SET_EVENT_STARTING_SOON, null);
    },
    async joinStudio(_, studioURL) {
      return await apiStudio.joinStudio(studioURL);
    },
    async optInToStudioMarketing(_, { studioURL, optIn }) {
      return await apiStudio.optInToStudioMarketing(studioURL, optIn);
    },
    async addStudentPhoneNumber(_, { studioURL, phone }) {
      return await apiStudio.addStudentPhoneNumber(studioURL, phone);
    },
    async loadTranslations({ commit }, studioURL) {
      const translations = await apiStudio.loadTranslations(studioURL);
      commit(types.SET_TRANSLATIONS, translations);
    },
    async saveTranslations({ state }) {
      const translations = pick(state.translations, ['translations']);
      await apiStudio.saveTranslations(translations);
    },
    async deleteAllTranslations() {
      await apiStudio.saveTranslations({ translations: null });
    },
    async getProductMedia(_, { studioURL, productId, mediaId }) {
      return await apiStudio.getProductMedia(studioURL, productId, mediaId);
    },
    async getPaymentPlanInvoices(_, { studioURL, paymentPlanId }) {
      return await apiStudio.getPaymentPlanInvoices(studioURL, paymentPlanId);
    },
    async updateStudentPaymentPlanPaymentMethod(
      _,
      { studioURL, id, defaultPaymentMethod }
    ) {
      return await apiStudio.updateStudentPaymentPlanPaymentMethod(
        studioURL,
        id,
        defaultPaymentMethod
      );
    },
    async completeTip(_, payload) {
      const {
        studioURL,
        defaultPaymentMethod,
        stripeCustomerId,
        tipAmount,
        reCaptchaToken,
      } = payload;
      const result = await apiStudio.completeTip(
        studioURL,
        defaultPaymentMethod,
        stripeCustomerId,
        tipAmount,
        reCaptchaToken
      );
      return result;
    },
    async createTipSetupIntent(_, { studioURL, payload }) {
      const setupIntent = await apiStudio.createTipSetupIntent(
        studioURL,
        payload
      );
      return setupIntent;
    },
    async getMarvelousBio({ commit }, studioURL: string) {
      const result = await apiStudio.getMarvelousBio(studioURL);
      commit(types.SET_MARVELOUS_BIO, result);
      return result;
    },
    async getLatestMediaViews({ commit }, studioURL: string) {
      const result = await apiStudio.getLatestMediaViews(studioURL);
      return result;
    },
    async getCourseProgress({ commit }, studioURL: string) {
      const result = await apiStudio.getCourseProgress(studioURL);
      return result;
    },
    async getWaiverToBeSigned({ commit }, { studioURL, uuid }) {
      const result = await apiStudio.getWaiverToBeSigned(studioURL, uuid);
      return result;
    },
    async getSignedContracts({ commit }, { studioURL }) {
      const result = await apiStudio.getSignedContracts(studioURL);
      return result;
    },
    async getPendingSignedContracts({ commit }, { studioURL }) {
      const result = await apiStudio.getPendingSignedContracts(studioURL);
      return result;
    },
    async getSignedContract({ commit }, { studioURL, uuid }) {
      const result = await apiStudio.getSignedContract(studioURL, uuid);
      return result;
    },
    async signContract({ commit }, { studioURL, uuid, signature, fullName }) {
      const result = await apiStudio.signContract(
        studioURL,
        uuid,
        signature,
        fullName
      );
      return result;
    },
    async getGift({ commit }, { studioURL, uuid }) {
      const result = await apiStudio.getGift(studioURL, uuid);
      return result;
    },
    async acceptGift({ commit }, { studioURL, uuid }) {
      const result = await apiStudio.acceptGift(studioURL, uuid);
      return result;
    },
    async loadGiftableProducts({ commit }, studioURL) {
      const result = await apiStudio.loadGiftableProducts(studioURL);
      return result;
    },
    async addPushToken({ commit }, { studioURL, token }) {
      const result = await apiStudio.addPushToken(studioURL, token);
      return result;
    },
    async pushTokenExists({ commit }, { studioURL, token }) {
      const result = await apiStudio.pushTokenExists(studioURL, token);
      return result;
    },
  },
  mutations: {
    [types.SET_RECENT_STUDIOS](
      state: State,
      recentStudios: StudioPreviewResponse[]
    ) {
      state.recentStudios = recentStudios;
    },
    [types.SET_CURRENT_STUDIO](state: State, currentStudio: StudioResponse) {
      state.currentStudio = currentStudio;
    },
    [types.SET_LESSON_MEDIA](state: State, data: LessonResponse) {
      state.currentLesson = data;
    },
    [types.SET_STUDIO_EVENTS](state: State, events: EventResponse[]) {
      state.studioEvents = events;
    },
    [types.SET_UPCOMING_STUDIO_EVENTS](state: State, events: EventResponse[]) {
      state.upcomingStudioEvents = events;
    },
    [types.RESET_STUDIO_STATE](state: State) {
      Object.assign(state, {}, initialState);
    },
    [types.SET_FAVOURITES](state: State, favourites: FavouritesResponse[]) {
      state.productMediaFavourites = favourites;
    },
    [types.SET_EVENT_STARTING_SOON](
      state: State,
      eventStartingSoon: EventResponse
    ) {
      state.eventStartingSoon = eventStartingSoon;
    },
    [types.SET_TRANSLATIONS](state: State, translations: TranslationsResponse) {
      state.translations = translations;
    },
    [types.SET_CURRENT_TRANSLATION_LANG](state: State, language: string) {
      state.currentTranslationsEditLang = language;
    },
    [types.SET_SINGLE_TRANSLATION](state: State, translation: any) {
      if (!state.translations) {
        return;
      }
      if (!state.translations.translations) {
        state.translations.translations = {};
      }
      set(
        state.translations.translations,
        translation.translationKey,
        translation.translationText
      );
    },
    [types.REMOVE_SINGLE_TRANSLATION](state: State, { translationKey }) {
      if (
        !state.translations ||
        !get(state.translations.translations, translationKey)
      ) {
        return;
      }
      set(state.translations.translations, translationKey, undefined);
    },
    [types.SET_MARVELOUS_BIO](state: State, data: BioResponse) {
      state.marvelousBio = { ...state.marvelousBio, ...data };
    },
  },
};

export default studioModule;
